import React from 'react';
import { Typography } from '@mui/material';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { SystemEventTypes } from '@flashbacktech/flashbackclient/api';

// Import notification icons
import checkNotifsIcon from '../assets/notifications/check-notifs.svg';
import reloadNotifsIcon from '../assets/notifications/reload-notifs.svg';
import warningNotifsIcon from '../assets/notifications/warning-notifs.svg';
import { useWithTokenRefresh } from '../utils/api';
import { TabIndex } from '../navigation';
import { useNavigate } from 'react-router-dom';

type SystemEvent = SystemEventTypes.SystemEventResponse;

function Activity() {
  const { authState, globalData } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const navigate = useNavigate();
  const [events, setEvents] = React.useState<SystemEvent[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  
  // Use centralized workspace hook
  const selectedWorkspaceId = useSelectedWorkspace();

  const loadEvents = React.useCallback(async () => {
    if (!authState.token) return;
    try {
      setLoading(true);
      const request: SystemEventTypes.SystemEventQueryRequest = {
        skip: 0,
        take: 100,
      };
      const res = await withTokenRefresh(() => apiClient.getSystemEvents(request));
      if (res && Array.isArray(res.events)) {
        setEvents(res.events);
      } else {
        setEvents([]);
      }
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [authState.token, withTokenRefresh, apiClient]);

  React.useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString();
    } catch {
      return '';
    }
  };
  const formatTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString();
    } catch {
      return '';
    }
  };

  // Helper: map workspace ID to display name (fallback to ID if unknown)
  const getWorkspaceName = (id?: string | null): string | null => {
    if (!id) return null;
    try {
      const list = globalData?.workspaces || [];
      const ws = Array.isArray(list) ? list.find((w: any) => w?.id === id) : undefined;
      return ws?.name || id;
    } catch {
      return id;
    }
  };

  // Helper: get icon for event type
  const getEventIcon = (event: string): string => {
    const evt = event.toLowerCase();
    if (evt === 'created') return checkNotifsIcon;
    if (evt === 'updated' || evt === 'changed') return reloadNotifsIcon;
    if (evt === 'deleted' || evt === 'invalid') return warningNotifsIcon;
    return checkNotifsIcon; // Default
  };

  // Helper: generate readable message for events
  const getEventMessage = (context: string, event: string, jsonData?: string | null): string => {
    const ctx = context.toLowerCase();
    const evt = event.toLowerCase();

    // Try to get name from jsonData for more context
    let itemName = '';
    try {
      if (jsonData) {
        const obj = JSON.parse(jsonData);
        itemName = obj.name || '';
      }
    } catch {
      // ignore
    }

    // Generate message based on context and event
    if (ctx === 'bucket') {
      if (evt === 'created')
        return itemName
          ? `Bucket "${itemName}" was successfully created`
          : 'Bucket was successfully created';
      if (evt === 'updated')
        return itemName ? `Bucket "${itemName}" was updated` : 'Bucket was updated';
      if (evt === 'deleted')
        return itemName ? `Bucket "${itemName}" was deleted` : 'Bucket was deleted';
    }

    if (ctx === 'repo' || ctx === 'repository') {
      if (evt === 'created')
        return itemName
          ? `Repository "${itemName}" was successfully created`
          : 'Repository was successfully created';
      if (evt === 'updated')
        return itemName ? `Repository "${itemName}" was updated` : 'Repository was updated';
      if (evt === 'deleted')
        return itemName ? `Repository "${itemName}" was deleted` : 'Repository was deleted';
    }

    if (ctx === 'apikey') {
      if (evt === 'created')
        return itemName ? `API Key "${itemName}" was created` : 'API Key was created';
      if (evt === 'updated')
        return itemName ? `API Key "${itemName}" was updated` : 'API Key was updated';
      if (evt === 'deleted')
        return itemName ? `API Key "${itemName}" was deleted` : 'API Key was deleted';
    }

    if (ctx === 'user') {
      if (evt === 'created') return 'User account was created';
      if (evt === 'updated') return 'User account was updated';
      if (evt === 'deleted') return 'User account was deleted';
    }

    if (ctx === 'usersettings') {
      if (evt === 'updated') return 'User settings were changed';
    }

    if (ctx === 'workspace') {
      if (evt === 'created')
        return itemName ? `Workspace "${itemName}" was created` : 'Workspace was created';
      if (evt === 'updated')
        return itemName ? `Workspace "${itemName}" was updated` : 'Workspace was updated';
      if (evt === 'deleted')
        return itemName ? `Workspace "${itemName}" was deleted` : 'Workspace was deleted';
    }

    if (ctx === 'organization') {
      if (evt === 'created') return 'Organization was created';
      if (evt === 'updated') return 'Organization settings were updated';
      if (evt === 'deleted') return 'Organization was deleted';
    }

    // Fallback to capitalize format
    const contextLabel = context.charAt(0).toUpperCase() + context.slice(1);
    const eventLabel = event.charAt(0).toUpperCase() + event.slice(1);
    return `${contextLabel} was ${eventLabel.toLowerCase()}`;
  };

  return (
    <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Titre de la page avec barre de recherche */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div>
              <Typography
                variant="h5"
                className="text-black"
                style={{
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '30px',
                }}
              >
                Recent Activity
              </Typography>
            </div>
            {/* Barre de recherche */}
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search activity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-80 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{
                  height: '40px',
                  fontSize: '14px',
                  color: '#374151',
                }}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Espacement réduit */}
          <div style={{ height: '30px' }}></div>

          {/* Tableau Activity */}
          <div className="relative flex justify-center">
            <div
              className="bg-white rounded-xl border border-[#E9EAEB] shadow-sm relative"
              style={{
                minHeight: '548px',
                width: '100%',
                maxWidth: '1200px',
                borderBottom: '1px solid #E9EAEB',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {/* Header Row - 44px height */}
              <div
                className="flex"
                style={{
                  height: '44px',
                  alignItems: 'center',
                  backgroundColor: 'white',
                  borderBottom: '1px solid #e5e7eb',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                }}
              >
                <div className="flex-1" style={{ paddingLeft: '16px' }}>
                  <Typography
                    variant="caption"
                    style={{
                      color: '#6B7280',
                      fontWeight: 600,
                      fontSize: '12px',
                      lineHeight: '18px',
                      textTransform: 'none',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Activity
                  </Typography>
                </div>
                <div className="hidden md:block" style={{ width: '132px', textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    style={{
                      color: '#6B7280',
                      fontWeight: 600,
                      fontSize: '12px',
                      lineHeight: '18px',
                      textTransform: 'none',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Date
                  </Typography>
                </div>
                <div className="hidden md:block" style={{ width: '129px', textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    style={{
                      color: '#6B7280',
                      fontWeight: 600,
                      fontSize: '12px',
                      lineHeight: '18px',
                      textTransform: 'none',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Timestamp
                  </Typography>
                </div>
                <div className="hidden md:block" style={{ width: '100px', textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    style={{
                      color: '#6B7280',
                      fontWeight: 600,
                      fontSize: '12px',
                      lineHeight: '18px',
                      textTransform: 'none',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Actions
                  </Typography>
                </div>
              </div>

              {/* Content Area */}
              <div
                className="overflow-y-auto"
                style={{
                  height: 'calc(548px - 44px)',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  borderTop: '1px solid #E9EAEB',
                }}
              >
                {!loading &&
                  events.filter((ev) => {
                    if (!searchQuery.trim()) return true;
                    const query = searchQuery.toLowerCase();
                    const ctx = ev.context.toLowerCase();
                    const evt = ev.event.toLowerCase();
                    if (ctx.includes(query) || evt.includes(query)) return true;
                    try {
                      if (ev.jsonData) {
                        const obj = JSON.parse(ev.jsonData);
                        const itemName = (obj.name || '').toLowerCase();
                        if (itemName.includes(query)) return true;
                      }
                    } catch {
                      /* ignore */
                    }
                    if (ev.workspaceId) {
                      const workspaceName = getWorkspaceName(ev.workspaceId);
                      if (workspaceName && workspaceName.toLowerCase().includes(query)) return true;
                    }
                    return false;
                  }).length === 0 && (
                    <div className="flex items-center justify-center" style={{ height: '100%' }}>
                      <Typography variant="body2" style={{ color: '#667085' }}>
                        {searchQuery.trim()
                          ? `No activity found matching "${searchQuery}"`
                          : 'No activity found for the selected filters and time window.'}
                      </Typography>
                    </div>
                  )}
                {(loading
                  ? []
                  : events.filter((ev) => {
                      // Filter events based on search query
                      if (!searchQuery.trim()) return true;

                      const query = searchQuery.toLowerCase();
                      const ctx = ev.context.toLowerCase();
                      const evt = ev.event.toLowerCase();

                      // Search in context and event
                      if (ctx.includes(query) || evt.includes(query)) return true;

                      // Search in item name from jsonData
                      try {
                        if (ev.jsonData) {
                          const obj = JSON.parse(ev.jsonData);
                          const itemName = (obj.name || '').toLowerCase();
                          if (itemName.includes(query)) return true;
                        }
                      } catch {
                        /* ignore */
                      }

                      // Search in workspace name
                      if (ev.workspaceId) {
                        const workspaceName = getWorkspaceName(ev.workspaceId);
                        if (workspaceName && workspaceName.toLowerCase().includes(query))
                          return true;
                      }

                      return false;
                    })
                ).map((ev, index) => (
                  <div
                    key={ev.id}
                    className="flex"
                    style={{
                      height: '72px',
                      alignItems: 'center',
                      backgroundColor: ev.showUnread ? 'rgba(97,123,255,0.06)' : '#FFFFFF',
                      borderBottom: index < events.length - 1 ? '1px solid #E9EAEB' : 'none',
                      borderLeft: ev.showUnread ? '3px solid #617BFF' : undefined,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = ev.showUnread ? 'rgba(97,123,255,0.06)' : '#FFFFFF';
                    }}
                  >
                    {/* Activity Section */}
                    <div
                      className="flex-1"
                      style={{
                        height: '72px',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        gap: '12px',
                      }}
                    >
                      {/* Event Icon */}
                      <img
                        src={getEventIcon(ev.event)}
                        alt=""
                        style={{
                          width: '40px',
                          height: '40px',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ width: '100%', flex: 1, minWidth: 0 }}>
                        {/* Title line */}
                        <div
                          style={{
                            color: '#111827',
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: '20px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {(() => {
                            // Parse jsonData to extract name
                            let itemName = '';
                            try {
                              if (ev.jsonData) {
                                const obj = JSON.parse(ev.jsonData);
                                itemName = obj.name || '';
                              }
                            } catch {
                              // ignore
                            }

                            const ctx = ev.context.toLowerCase();
                            const evt = ev.event.toLowerCase();

                            // Determine type label
                            let typeLabel = '';
                            if (ctx === 'bucket') typeLabel = 'Bucket';
                            else if (ctx === 'workspace') typeLabel = 'Workspace';
                            else if (ctx === 'repo' || ctx === 'repository')
                              typeLabel = 'Repository';
                            else if (ctx === 'apikey') typeLabel = 'API Key';
                            else if (ctx === 'user') typeLabel = 'User account';
                            else if (ctx === 'usersettings') typeLabel = 'User settings';
                            else if (ctx === 'organization') typeLabel = 'Organization';
                            else
                              typeLabel = ev.context.charAt(0).toUpperCase() + ev.context.slice(1);

                            // Determine action
                            let action = '';
                            if (evt === 'created')
                              action =
                                ctx === 'bucket' || ctx === 'repo'
                                  ? 'was successfully created'
                                  : 'was created';
                            else if (evt === 'updated')
                              action = ctx === 'usersettings' ? 'were changed' : 'was updated';
                            else if (evt === 'deleted') action = 'was deleted';
                            else action = `was ${evt}`;

                            // Handle navigation
                            const handleItemClick = (e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (ctx === 'bucket') {
                                navigate('/cloud-storage/buckets');
                              } else if (ctx === 'repo' || ctx === 'repository') {
                                navigate('/repositories/inventory');
                              } else if (ctx === 'apikey') {
                                navigate('/cloud-storage/overview');
                              } else if (ctx === 'workspace') {
                                navigate('/workspace/home');
                              } else if (ctx === 'organization') {
                                navigate('/organization');
                              } else if (ctx === 'usersettings' || ctx === 'user') {
                                window.dispatchEvent(new CustomEvent('openProfileModal', { detail: { section: 'Profile' } }));
                              }
                            };

                            // Render message with clickable name
                            if (itemName) {
                              return (
                                <>
                                  {typeLabel}{' '}
                                  <span
                                    onClick={handleItemClick}
                                    style={{
                                      color: '#4766FF',
                                      cursor: 'pointer',
                                      fontWeight: 600,
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.textDecoration = 'underline')
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.textDecoration = 'none')
                                    }
                                  >
                                    "{itemName}"
                                  </span>{' '}
                                  {action}
                                </>
                              );
                            } else {
                              return `${typeLabel} ${action}`;
                            }
                          })()}
                        </div>
                        {/* Meta line: workspace if present */}
                        {ev.workspaceId && (
                          <div
                            style={{
                              color: '#6B7280',
                              fontSize: 12,
                              lineHeight: '18px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            Workspace: {getWorkspaceName(ev.workspaceId)}
                          </div>
                        )}
                        {/* Summary line from jsonData - only relevant metadata */}
                        {(() => {
                          try {
                            if (!ev.jsonData) return null;
                            const obj = JSON.parse(ev.jsonData);
                            const parts: string[] = [];

                            // For buckets, show storage details
                            if (ev.context === 'bucket') {
                              if (obj.storageType || obj.storage_type)
                                parts.push(`${obj.storageType || obj.storage_type}`);
                              if (obj.region) parts.push(obj.region);
                            }

                            // For API keys, show description if available
                            if (ev.context === 'apikey' && obj.description) {
                              parts.push(obj.description);
                            }

                            // For user settings, show user email
                            if (ev.context === 'usersettings') {
                              const userEmail = obj.email || obj.userEmail || authState?.user?.email;
                              if (userEmail) {
                                parts.push(`Modified by: ${userEmail}`);
                              }
                            }

                            const summary = parts.join(' • ');
                            if (!summary) return null;
                            return (
                              <div
                                style={{
                                  color: '#6B7280',
                                  fontSize: 12,
                                  lineHeight: '18px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {summary}
                              </div>
                            );
                          } catch {
                            return null;
                          }
                        })()}
                      </div>
                    </div>

                    {/* Date Section - 132px */}
                    <div
                      className="hidden md:flex"
                      style={{
                        width: '132px',
                        height: '72px',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{
                          color: '#667085',
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '20px',
                        }}
                      >
                        {formatDate(ev.timestamp)}
                      </Typography>
                    </div>

                    {/* Timestamp Section - 129px */}
                    <div
                      className="hidden md:flex"
                      style={{
                        width: '129px',
                        height: '72px',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{
                          color: '#667085',
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '20px',
                        }}
                      >
                        {formatTime(ev.timestamp)}
                      </Typography>
                    </div>

                    {/* Mark as read / unread */}
                    <div
                      className="hidden md:flex"
                      style={{
                        width: '100px',
                        height: '72px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingRight: '8px',
                      }}
                    >
                      {ev.showUnread ? (
                        <button
                          type="button"
                          className="text-[12px] text-[#617BFF] hover:underline"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await withTokenRefresh(() => apiClient.markSystemEventAsRead(ev.id));
                              setEvents((prev) =>
                                prev.map((e) => (e.id === ev.id ? { ...e, showUnread: false } : e))
                              );
                            } catch {
                              /* ignore */
                            }
                          }}
                        >
                          Mark as read
                        </button>
                      ) : (
                        <span className="text-[12px] text-[#22C55E] flex items-center gap-0.5">
                          ✓ Read
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Activity;
